import type { Difficulty, MCQQuestion } from '../math/types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

interface Template {
  build(rand: () => number, d: Difficulty): { prompt: string; correct: number; explanation: string };
}

// T1: 買物品找零（乘法＋減法）
const t1: Template = {
  build(rand, d) {
    const X = d === 1 ? randInt(rand, 3, 8) : d === 2 ? randInt(rand, 5, 15) : randInt(rand, 8, 20);
    const Y = d === 1 ? randInt(rand, 12, 30) : d === 2 ? randInt(rand, 20, 60) : randInt(rand, 30, 80);
    const cost = X * Y;
    const paid = cost + randInt(rand, 1, 5) * 100;
    const correct = paid - cost;
    return {
      prompt: `媽媽買了 ${X} 件衣服，每件 ${Y} 元，付了 ${paid} 元，找回多少元？`,
      correct,
      explanation: `${X}×${Y}=${cost} 元，${paid}−${cost}=${correct} 元`,
    };
  },
};

// T2: 分組包裝（乘法＋加法）
const t2: Template = {
  build(rand, d) {
    const groups = d === 1 ? randInt(rand, 4, 8) : d === 2 ? randInt(rand, 5, 12) : randInt(rand, 8, 20);
    const perGroup = d === 1 ? randInt(rand, 8, 15) : d === 2 ? randInt(rand, 10, 25) : randInt(rand, 15, 30);
    const extra = randInt(rand, 3, 10);
    const correct = groups * perGroup + extra;
    return {
      prompt: `工廠有 ${groups} 箱蘋果，每箱 ${perGroup} 顆，另外散裝 ${extra} 顆，共有幾顆蘋果？`,
      correct,
      explanation: `${groups}×${perGroup}=${groups * perGroup} 顆，再加 ${extra} 顆，共 ${correct} 顆`,
    };
  },
};

// T3: 計算剩餘（乘法＋減法）
const t3: Template = {
  build(rand, d) {
    const total = d === 1 ? randInt(rand, 50, 150) : d === 2 ? randInt(rand, 100, 400) : randInt(rand, 200, 800);
    const each = d === 1 ? randInt(rand, 5, 10) : d === 2 ? randInt(rand, 8, 20) : randInt(rand, 10, 30);
    const days = d === 1 ? randInt(rand, 3, 6) : d === 2 ? randInt(rand, 4, 8) : randInt(rand, 5, 10);
    const used = each * days;
    if (used >= total) {
      // fallback: ensure enough supply
      const fallbackTotal = total + used;
      const fallbackCorrect = total;
      return {
        prompt: `倉庫有 ${fallbackTotal} 包米，每天用 ${each} 包，用了 ${days} 天，還剩幾包？`,
        correct: fallbackCorrect,
        explanation: `用了 ${each}×${days}=${used} 包，${fallbackTotal}−${used}=${fallbackCorrect} 包`,
      };
    }
    const correct = total - used;
    return {
      prompt: `倉庫有 ${total} 包米，每天用 ${each} 包，用了 ${days} 天，還剩幾包？`,
      correct,
      explanation: `用了 ${each}×${days}=${used} 包，${total}−${used}=${correct} 包`,
    };
  },
};

// T4: 兩步驟乘除應用（除法＋乘法）
const t4: Template = {
  build(rand, d) {
    const divisor = d === 1 ? randInt(rand, 3, 5) : d === 2 ? randInt(rand, 4, 8) : randInt(rand, 5, 10);
    const quotient = d === 1 ? randInt(rand, 4, 8) : d === 2 ? randInt(rand, 5, 12) : randInt(rand, 8, 20);
    const total = divisor * quotient;
    const price = d === 1 ? randInt(rand, 5, 15) : d === 2 ? randInt(rand, 10, 30) : randInt(rand, 15, 50);
    const correct = quotient * price;
    return {
      prompt: `共有 ${total} 本書，平均分給 ${divisor} 個班，每班再每本賣 ${price} 元，每班共收多少元？`,
      correct,
      explanation: `${total}÷${divisor}=${quotient} 本，${quotient}×${price}=${correct} 元`,
    };
  },
};

// T5: 面積應用（乘法＋加法）
const t5: Template = {
  build(rand, d) {
    const w1 = d === 1 ? randInt(rand, 5, 10) : d === 2 ? randInt(rand, 8, 15) : randInt(rand, 10, 20);
    const h1 = d === 1 ? randInt(rand, 4, 8) : d === 2 ? randInt(rand, 6, 12) : randInt(rand, 8, 18);
    const w2 = d === 1 ? randInt(rand, 3, 7) : d === 2 ? randInt(rand, 5, 10) : randInt(rand, 6, 14);
    const h2 = d === 1 ? randInt(rand, 3, 6) : d === 2 ? randInt(rand, 4, 9) : randInt(rand, 5, 12);
    const correct = w1 * h1 + w2 * h2;
    return {
      prompt: `花圃甲長 ${w1} 公尺寬 ${h1} 公尺，花圃乙長 ${w2} 公尺寬 ${h2} 公尺，兩個花圃面積共幾平方公尺？`,
      correct,
      explanation: `甲：${w1}×${h1}=${w1 * h1}，乙：${w2}×${h2}=${w2 * h2}，合計 ${correct} 平方公尺`,
    };
  },
};

const TEMPLATES: Template[] = [t1, t2, t3, t4, t5];

/**
 * N-4-4：兩步驟乘除應用題（四上版）。
 * 四上第四章：乘除法應用、多步驟解題。
 */
export function generateTwoStepUp(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);
  const tIdx = Math.floor(rand() * TEMPLATES.length);
  const template = TEMPLATES[tIdx]!;
  const { prompt, correct, explanation } = template.build(rand, difficulty);

  const distractors = new Set<number>();
  const offsets = [
    Math.floor(rand() * 50) + 10,
    Math.floor(rand() * 80) + 20,
    Math.floor(rand() * 120) + 30,
  ];
  for (const off of offsets) {
    const c = correct + (rand() > 0.5 ? off : -off);
    if (c > 0 && c !== correct) distractors.add(c);
  }
  let extra = 7;
  while (distractors.size < 3) {
    const c = correct + extra;
    if (c > 0 && !distractors.has(c)) distractors.add(c);
    extra += 7;
  }

  const optionList = [correct, ...Array.from(distractors).slice(0, 3)];
  const shuffled = [...optionList].sort((x, y) => mulberry32(seed + x)() - mulberry32(seed + y)());

  return {
    kind: 'mcq',
    prompt,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation,
    kp: 'math.word.two-step.4up',
  };
}
